import type { ReactNode } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { ChartCard } from './ChartCard';
import {
  CATEGORICAL_COLORS,
  CHART_COLORS,
  axisTick,
  formatPeriod,
  useChartLocale,
  type PeriodGrouping,
} from './chartTheme';

interface TimelineDataPoint {
  date: string;
  count: number;
}

interface TimelineChartProps {
  data: TimelineDataPoint[];
  title?: string;
  color?: string;
  height?: number;
  /** Tooltip label for the series (defaults to "Nombre"). */
  seriesLabel?: string;
  /** Grouping of the date keys; enables month-name labels for 'month'. */
  groupBy?: PeriodGrouping;
  emptyMessage?: string;
  className?: string;
  /** Controls rendered in the card header (e.g. a group-by toggle). */
  actions?: ReactNode;
}

export function TimelineChart({
  data,
  title,
  color = CHART_COLORS.primary,
  height = 300,
  seriesLabel,
  groupBy,
  emptyMessage,
  className,
  actions,
}: TimelineChartProps) {
  const { t } = useTranslation();
  const { locale, isRtl, formatNumber, tooltip } = useChartLocale();
  const gradientId = `timeline-${useId().replace(/:/g, '')}`;

  return (
    <ChartCard
      title={title}
      actions={actions}
      isEmpty={data.length === 0}
      emptyMessage={emptyMessage}
      className={className}
    >
      <div dir="ltr">
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.18} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={axisTick}
              tickLine={false}
              axisLine={{ stroke: CHART_COLORS.grid }}
              reversed={isRtl}
              tickFormatter={(v) => formatPeriod(v, locale, false, groupBy)}
              minTickGap={16}
            />
            <YAxis
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              width={44}
              orientation={isRtl ? 'right' : 'left'}
              tickFormatter={formatNumber}
              allowDecimals={false}
            />
            <Tooltip
              {...tooltip}
              cursor={{ stroke: CHART_COLORS.grid }}
              labelFormatter={(v) => formatPeriod(v, locale, true, groupBy)}
              formatter={(value) => [formatNumber(value), seriesLabel ?? t('charts.count')]}
            />
            <Area
              type="monotone"
              dataKey="count"
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradientId})`}
              dot={data.length <= 31 ? { r: 3, fill: color, strokeWidth: 0 } : false}
              activeDot={{ r: 5, strokeWidth: 2, stroke: '#ffffff' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}

// Multi-line chart for comparing trends
interface MultiLineChartProps {
  data: { atcCode: string; atcName: string; timeline: TimelineDataPoint[] }[];
  title?: string;
  height?: number;
  groupBy?: PeriodGrouping;
  emptyMessage?: string;
  className?: string;
}

const MAX_SERIES = 6;

export function MultiLineChart({
  data,
  title,
  height = 400,
  groupBy,
  emptyMessage,
  className,
}: MultiLineChartProps) {
  const { locale, isRtl, formatNumber, tooltip, legendStyle } = useChartLocale();
  const series = data.slice(0, MAX_SERIES);

  // Merge all timelines into a single dataset
  const allDates = new Set<string>();
  series.forEach((d) => d.timeline.forEach((p) => allDates.add(p.date)));

  const mergedData = Array.from(allDates)
    .sort()
    .map((date) => {
      const point: Record<string, string | number> = { date };
      series.forEach((d) => {
        const found = d.timeline.find((p) => p.date === date);
        point[d.atcCode] = found?.count || 0;
      });
      return point;
    });

  return (
    <ChartCard
      title={title}
      isEmpty={mergedData.length === 0}
      emptyMessage={emptyMessage}
      className={className}
    >
      <div dir="ltr">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={mergedData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid stroke={CHART_COLORS.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="date"
              tick={axisTick}
              tickLine={false}
              axisLine={{ stroke: CHART_COLORS.grid }}
              reversed={isRtl}
              tickFormatter={(v) => formatPeriod(v, locale, false, groupBy)}
              minTickGap={16}
            />
            <YAxis
              tick={axisTick}
              tickLine={false}
              axisLine={false}
              width={44}
              orientation={isRtl ? 'right' : 'left'}
              tickFormatter={formatNumber}
              allowDecimals={false}
            />
            <Tooltip
              {...tooltip}
              cursor={{ stroke: CHART_COLORS.grid }}
              labelFormatter={(v) => formatPeriod(v, locale, true, groupBy)}
              formatter={(value) => formatNumber(value)}
            />
            <Legend iconType="circle" iconSize={8} wrapperStyle={legendStyle} />
            {series.map((d, i) => (
              <Line
                key={d.atcCode}
                type="monotone"
                dataKey={d.atcCode}
                name={d.atcName}
                stroke={CATEGORICAL_COLORS[i % CATEGORICAL_COLORS.length]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: '#ffffff' }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </ChartCard>
  );
}
